import { Component } from '@theme/component';

/**
 * A small utility for setting up an animated disclosure panel using inert and CSS display grid for height auto animation.
 *
 * This component works with any element that follows the details/summary pattern for revealing content.
 * Forms and other interactive elements don't need to live inside a details element, but can still use
 * the expanded pattern for accessibility and animation.
 *
 * The component relies on CSS display grid and inert attributes to create smooth height animations
 * when expanding/collapsing content areas.
 *
 * @class DisclosureCustom
 * @typedef {object} Refs
 * @property {HTMLElement} disclosureTrigger – The disclosure trigger element.
 * @property {HTMLElement} disclosureContent – The disclosure content element.
 *
 * @extends Component<Refs>
 */
class DisclosureCustom extends Component {
  requiredRefs = ['disclosureTrigger', 'disclosureContent'];

  /** @type {MutationObserver | null} */
  #observer = null;

  /** @type {boolean} */
  #restoring = false;

  connectedCallback() {
    super.connectedCallback();

    // Memoria del abierto/cerrado — SOLO para quien la pide con
    // data-remember-key. El caso que la motivó: la caja de "Descuento" del
    // carrito calcula su estado en el servidor a partir de si ya hay un cupón
    // aplicado, así que CADA re-render del carrito (cambiar cantidad, agregar
    // algo, aplicar el cupón) la devolvía a cerrada mientras el cliente
    // escribía. Sólo se restaura ABIERTA: si el servidor ya la manda abierta
    // porque hay cupón, no la cerramos nunca.
    if (!this.#rememberKey) return;

    if (this.#readRemembered() === '1') this.#setExpanded(true);

    // El carrito no se REEMPLAZA, se MORPHEA: los nodos son los mismos y sólo
    // se les repone el atributo que trae el servidor. Por eso connectedCallback
    // no vuelve a correr y no basta con restaurar al conectar: hay que vigilar
    // el aria-expanded y devolverlo a abierto cuando el morph lo cierre.
    // El usuario cerrándola pasa por toggleDisclosure, que apunta '0' en la
    // memoria ANTES — así que esto nunca le pelea un cierre suyo.
    this.#observer = new MutationObserver(() => {
      if (this.#restoring) return;
      if (this.#readRemembered() !== '1') return;
      const { disclosureTrigger: trigger } = this.refs;
      if (!trigger || trigger.matches('[aria-expanded="true"]')) return;

      this.#restoring = true;
      this.#setExpanded(true);
      this.#restoring = false;
    });
    this.#observer.observe(this, { attributes: true, subtree: true, attributeFilter: ['aria-expanded'] });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#observer = null;
  }

  get #rememberKey() {
    return this.dataset.rememberKey || '';
  }

  #readRemembered() {
    try {
      return sessionStorage.getItem(this.#rememberKey);
    } catch {
      return null;
    }
  }

  #remember(expanded) {
    if (!this.#rememberKey) return;
    try {
      sessionStorage.setItem(this.#rememberKey, expanded ? '1' : '0');
    } catch {
      // Modo privado o cookies bloqueadas: la caja sigue funcionando, sólo no recuerda.
    }
  }

  /**
   * Applies the open/closed state to the trigger and the content.
   * @param {boolean} expanded - Whether the panel should end up open.
   */
  #setExpanded(expanded) {
    const { disclosureTrigger: trigger, disclosureContent: content } = this.refs;
    if (!trigger || !content) return;
    if (trigger.matches('[aria-expanded="true"]') === expanded) return;

    trigger.setAttribute('aria-expanded', String(expanded));
    trigger.setAttribute(
      'aria-label',
      `${expanded ? trigger.dataset.disclosureClose : trigger.dataset.disclosureOpen}`
    );
    content.inert = !expanded;
  }

  /**
   * Toggles the disclosure panel open/closed state.
   * Updates aria-expanded, aria-label attributes on the trigger and inert state on content.
   * Works with CSS grid animations to provide smooth height transitions.
   */
  toggleDisclosure = () => {
    const { disclosureTrigger: trigger } = this.refs;
    const expanded = trigger.matches('[aria-expanded="true"]');
    // La memoria PRIMERO: el observador de arriba lee justo este valor para
    // decidir si repone el abierto, y si lo escribiéramos después le pelearía
    // al cliente el cierre que acaba de pedir.
    this.#remember(!expanded);
    this.#setExpanded(!expanded);
  };
}

if (!customElements.get('disclosure-custom')) {
  customElements.define('disclosure-custom', DisclosureCustom);
}
